import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  TextField,
  MenuItem,
  TableFooter,
  Snackbar,
  Alert,
  Skeleton,
} from '@mui/material';
import { useCancelInviteMutation, useFollowUpMutation, useLazyGetAssessmentRecordsQuery, useLazyGetCandidateDetailsQuery } from '@/redux/apis/manage-folder';
import { getFolderIdFromURL } from '@/utils/getFolderId';
import FilterIcon from '@/icons/FilterIcon';
import SortIcon from '@/icons/SortIcon';
import TablePaginationActions from '@/components/Table/TablePagination';
import { SEARCH_BIG } from '@/icons/Search';
import { extractDomain, formatDateWithoutTime } from '@/utils/index';
import styles from './styles.module.css'
import MainLoaderIcon from './MainLoaderIcon';
import ArrowRight from '@/icons/ArrowRight';

const FOLLOW_UP_VAL = "Follow-up";
const CANCEL_INVITE_VAL = "Cancel Invite";
const VIEW_REPORT = 'View Report';

export interface CandidateEntry {
  id: number;
  candidateName: string;
  status: string;
  statusColor: string;
  statusDate: string;
  actionType: 'Invite' | 'Follow-up' | null;
  additionalAction: 'View Report' | 'Cancel Invite' | null;
  puuid: string;
  reportUrl?: string;
  uuid?: string | number;
}

type Props = {
  assessmentCampaignId?: string | null;
};

const TableComponent: React.FC<Props> = ({ assessmentCampaignId }) => {
  const [data, setData] = useState<CandidateEntry[]>([]);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('Status'); //statusDate
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0); // Current page state (0-based index)
  const [showMainLoader, setShowMainLoader] = useState(true);
  const [apiCounter, setApiCounter] = useState(0);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [isToastSuccess, setIsToastSuccess] = useState(true);
  const [toastText, setToastText] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  const [triggerGetCandidateDetails, { data: apiData, isLoading, isError, isSuccess }] =
    useLazyGetCandidateDetailsQuery();
    const [ triggerFollowUp, { isLoading: followUpLoading }] = useFollowUpMutation();
    const [ triggerCancelAssessment,{ isLoading: cancelApiLoading} ] = useCancelInviteMutation();
    const [triggerRecords, { data: records }] = useLazyGetAssessmentRecordsQuery();

    const handleChangePage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ) => {
      setCurrentPage(newPage);
    };

    const payload = {
      candidateId: null,
      page: currentPage + 1, // Convert to 1-based index for API
      campaignId: assessmentCampaignId,
      folderId: getFolderIdFromURL(),
      candidateName: search,
      filter: filter === 'all' ? '' : filter,
      sortBy: sort,
    };

    const triggerTableApi = async()=>{
      if(typeof assessmentCampaignId === "number" ){ 
        if(apiCounter===0) setShowMainLoader(true);
        // Trigger API call whenever page, filter, sort, or search changes
        setTableLoading(true);
        const res = await triggerGetCandidateDetails(payload);
        setTableLoading(false);
        if(res?.data?.length>0) {
          setShowMainLoader(false);
          setApiCounter(apiCounter+1);
        }
      }
    }

    const handleAction = async(entry: any) => {
        if(entry?.actionType === FOLLOW_UP_VAL && !followUpLoading){
          const response = await triggerFollowUp({
            assessmentCampaignId: null,
            assessmentProfileDetailsId: entry?.id,
            status: "followup",
            type: "single",
          }).unwrap();
  
  
          if (response?.success) {
            setIsToastSuccess(true);
            setToastText('Your followup request is being processed. Please check back in a few minutes.');
            setIsToastOpen(true);
            triggerTableApi();
          } else {
            setIsToastSuccess(false);
            setToastText('Something went wrong');
            setIsToastOpen(true);
          }
        }
    }


    const handleAdditional = async(entry: any) => {
      if(entry?.additionalAction === CANCEL_INVITE_VAL && !cancelApiLoading){
        const response = await triggerCancelAssessment({
          assessmentCampaignId: null,
          assessmentProfileDetailsId: entry?.id,
          status: "cancel",
          type: "single",
        }).unwrap();

        if (response?.success) {
          setIsToastSuccess(true);
          setToastText('Your cancellation request is being processed. Please check back in a few minutes.');
          setIsToastOpen(true);
          triggerTableApi()
        } else {
          setIsToastSuccess(false);
          setToastText('Something went wrong');
          setIsToastOpen(true);
        }
      }else if(entry?.additionalAction === VIEW_REPORT){
        window.open(`${extractDomain()}/edge/user-management/assessmentReport?uuid=${entry?.uuid}&&isATS=0`,
        "_blank")
      }
      
  }


  useEffect(() => {
    if (typeof assessmentCampaignId === "number") {
      triggerTableApi();
     
      triggerRecords({
        campaignId: assessmentCampaignId,
        folderId: getFolderIdFromURL(),
        candidateName: search,
        filter: filter === 'all' ? '' : filter,
        sortBy: sort,
      })
    }
  }, [triggerGetCandidateDetails, filter, sort, assessmentCampaignId, currentPage, search]);

  useEffect(() => {
    if (apiData) {
      const processedData = apiData.map((item: any): CandidateEntry => {
        const id = item?.id;
           
        let status = item.assessmentStatus;
            if(item?.assessmentStatus==="completed"){
              status = item.recommendationStatus;
            }else if(item?.assessmentStatus?.toLowerCase() === "playing"){    
              status = "In Progress"
            }

        let actionType: CandidateEntry['actionType'] = null
        let additionalAction: CandidateEntry['additionalAction'] = null

        if ((item.assessmentStatus)?.toLowerCase() === 'invited') {
          actionType = FOLLOW_UP_VAL;
          additionalAction = CANCEL_INVITE_VAL;
        } else if (['completed', 'disqualified'].includes(item?.assessmentStatus?.toLowerCase()) && item?.reportUrl?.length>0) {
          additionalAction = VIEW_REPORT;
        }

        return {
          id,
          candidateName: item.candidateName,
          status,
          statusColor:
            status === 'Recommended'
              ? '#4caf50'
              : status === 'Highly Recommended'
              ? '#2BA682'
              : status === 'Not Recommended'
              ? '#E8E8EB'
              : status === 'invited'
              ? '#FEDEA4'
              : status === 'In Progress'
              ? '#F1E6F9'
              : status === 'Cautiously Recommended'
              ? '#BCDCAB'
              : '#E8E8EB',
          statusDate: item?.updatedDate || item?.createdDate || 'N/A',
          actionType,
          additionalAction,
          puuid: item?.puuid,
          reportUrl: item?.reportUrl,
          uuid: item?.uuid,
        };
      });
      setData(processedData);
      // setTotalItems(apiData.totalItems || 0); // Set the total items count
    }
  }, [apiData]);


  const MAX_API_CALL_COUNT = 3;
  const tableHeaderStyles= {
    fontSize: '14px',
    fontWeight: 700,
  }

  const tableCellStyles = {
    "&.MuiTableCell-body": {
      borderBottom: '2px solid #e6ebef',
    }
    };

  useEffect(() => {
    let intervalId: any;
  
    if (apiData?.length === 0) { // Initial call
      intervalId = setInterval(() => {
        if (typeof assessmentCampaignId === "number") {
          triggerGetCandidateDetails(payload);
          setApiCounter((prev: number) => prev + 1); // Update via state
        }
      }, 10000);
    }

    // setShowMainLoader(apiCounter <= MAX_API_CALL_COUNT);
  
    if (apiData?.length > 0 || apiCounter === MAX_API_CALL_COUNT || filter !== "all") {
      clearInterval(intervalId);
      setShowMainLoader(false);
    }
  
    return () => {
      clearInterval(intervalId); // Cleanup
    };
  }, [apiData, apiCounter]);

  useEffect(()=>{setCurrentPage(0);},[filter, sort, search, assessmentCampaignId]) // resetting the pagination

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '60%' }}>
      {/* Search, Filter, and Sort */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 18px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          // boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          marginBottom: '16px',
        }}
      >
        {/* Search Field */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '200px' }}>
          <TextField
            placeholder="Search for candidate"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <>{SEARCH_BIG}</>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                width: 200,
                height: '40px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 400,
                boxShadow: 'none',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #6e00be', // Focus border color
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #6e00be', // Hover border color
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #c3c3c3', // Default border color
              },
              '& .MuiInputBase-input': {
                padding: '12px',
              },
            }}
          />
        </Box>

       <Box sx={{display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
        {/* Filter Dropdown */}
       <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', width: 208 }}>
          <FilterIcon />
          <Typography sx={{ color: '#17142A', fontSize: '12px' }}>
            Filter by :
          </Typography>
         <Box sx={{position: 'relative'}}>
         <TextField
            select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{
              border: filter?.length > 0 ? 'none' : '1px solid #c3c3c3',
              maxWidth: '120px',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                border: 'none',
                boxShadow: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': {
                fontSize: '12px',
                fontWeight: 400,
                color: '#1D1934',
                padding: '4px 14px 0 4px !important',
              },
              '& .MuiSelect-icon': { display: 'none !important' },
            }}
            SelectProps={{
              MenuProps: {
                MenuListProps: {
                  sx: {
                    padding: 0,
                  },
                },
              },
            }}
          >
            <MenuItem value="all"  sx={{fontSize: '12px'}}>All Candidates</MenuItem>
            <MenuItem value="invited"  sx={{fontSize: '12px'}}>Invited</MenuItem>
            <MenuItem value="playing"  sx={{fontSize: '12px'}}>In Progress</MenuItem>
            <MenuItem value="Highly Recommended"  sx={{fontSize: '12px'}}>Highly Recommended</MenuItem>
            <MenuItem value="Recommended"  sx={{fontSize: '12px'}}>Recommended</MenuItem>
            <MenuItem value="Cautiously Recommended"  sx={{fontSize: '12px'}}>Cautiously Recommended</MenuItem>
            <MenuItem value="Not Recommended"  sx={{fontSize: '12px'}}>Not Recommended</MenuItem>
            <MenuItem value="cancelled"  sx={{fontSize: '12px'}}>Cancelled</MenuItem>
            <MenuItem value="disqualified"  sx={{fontSize: '12px'}}>Disqualified</MenuItem>
          </TextField>
           <div className={styles.filter_down_icon}><ArrowRight/></div>
         </Box>
        </Box>

        {/* Sort Dropdown */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', width: 208 }}>
          <SortIcon />
          <Typography sx={{ color: '#17142A', fontSize: '12px' }}>
            Sort by :
          </Typography>
          <Box sx={{position: 'relative'}}>
          <TextField
            select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            sx={{
              maxWidth: '120px',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                border: 'none',
                boxShadow: 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-select': {
                fontSize: '12px',
                fontWeight: 400,
                color: '#1D1934',
                padding: '4px 14px 0 4px !important',
              },
              '& .MuiSelect-icon': { display: 'none !important' },
            }}
            SelectProps={{
              MenuProps: {
                MenuListProps: {
                  sx: {
                    padding: 0,
                  },
                },
              },
            }}
          >
            <MenuItem value="Recommendation" sx={{fontSize: '12px'}}>Recommendation</MenuItem>
            <MenuItem value="Status" sx={{fontSize: '12px'}}>Status date</MenuItem>
          </TextField>
          <div className={styles.filter_down_icon}><ArrowRight/></div>
          </Box>
        </Box>
       </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          flex: 1,
          overflow: 'auto',
          // boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fff', height: '44px' }}>
              <TableCell sx={tableHeaderStyles}>Candidate Name</TableCell>
              <TableCell sx={tableHeaderStyles}>Status</TableCell>
              <TableCell sx={tableHeaderStyles}>Status Date</TableCell>
              <TableCell sx={tableHeaderStyles}>Action</TableCell>
              <TableCell sx={tableHeaderStyles}>Additional Action</TableCell>
            </TableRow>
          </TableHead>
        {!showMainLoader && <TableBody>
            { 
            // Table Loader
             tableLoading ? <>
                {Array.from({ length: 10 }, (_, i) => i + 1).map(i => <TableRow key={i} sx={{ backgroundColor: '#fafafa', height: '66px' }}>
                  {[1,2,3,4,5].map(cell=><TableCell>
                    <Skeleton variant="rounded" width='100%' height={24} />
                  </TableCell>)}
                </TableRow>)}
             </>
            : data.map((entry, index) => (
              <TableRow key={index} sx={{ backgroundColor: '#fafafa', height: '72px' }}>
                <TableCell sx={{...tableCellStyles, width: '200px' }}>
                  <Typography variant="body2" color="#6E00BE" fontSize={12} fontWeight={400} 
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                    onClick={() => {
                      window.open(
                        `${extractDomain()}/edge/recruiter-cdp/profile?cn=in&p_uuid=${entry?.puuid}&psc=rexmonster`,
                        "_blank"
                      )
                    }}
                  >
                    {entry.candidateName}
                  </Typography>
                </TableCell>
                <TableCell sx={{...tableCellStyles, width: '200px' }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      padding: '4px',
                      minHeight: '24px',
                      backgroundColor: entry?.status?.length > 0 ? entry.statusColor : 'none',
                      borderRadius: '4px',
                      color: ['Recommended', 'Highly Recommended'].includes(entry?.status) ? '#fff' : 'black',
                      fontSize: '12px',
                      fontWeight: 400,
                      textTransform: 'capitalize',
                    }}
                  >
                    {entry.status}
                  </Box>
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  <Typography variant="body2" color="#59566C" fontSize={12} fontWeight={400}>
                    {formatDateWithoutTime(entry.statusDate)}
                  </Typography>
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: entry.actionType ? '#6E00BE' : '#9e9e9e',
                      cursor: !entry.actionType ? 'default' : (followUpLoading ? "wait" : "pointer"),
                      opacity: followUpLoading ? 0.5 : 1,
                    }}
                    onClick={() => handleAction(entry)}
                  >
                    {entry.actionType || ''}
                  </Typography>
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: entry.additionalAction ? '#6E00BE' : '#9e9e9e',
                      cursor: !entry.additionalAction ? 'default' : (cancelApiLoading ? "wait" : "pointer"),
                      textTransform: 'capitalize',
                      opacity: cancelApiLoading ? 0.5 : 1,
                    }}
                    onClick={() => handleAdditional(entry)}
                  >
                    {entry.additionalAction || ''}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>}
         {(apiData?.length >0 && !isLoading && !showMainLoader) && <TableFooter>
            <TableRow>
              <TablePaginationActions
                colSpan={6}
                count={records || 0}
                rowsPerPage={10}
                page={currentPage}
                onPageChange={handleChangePage}
                whiteBackgroundClr={true}
              />
            </TableRow>
          </TableFooter>}
        </Table>
      </TableContainer>
        {apiData?.length ===0 && !showMainLoader && (<div className={styles.no_data_table}>{SEARCH_BIG} No data found</div>)}

         {/* Table Main Loader  */}
       {showMainLoader && <Box className={styles.main_loader}>
            <Box><MainLoaderIcon/></Box>
            <Box sx={{fontWeight: 500, fontSize: '16px', margin: "8px 0"}}>Analyzing responses for actionable insights…</Box>
        </Box>}


        <Snackbar
        open={isToastOpen}
        autoHideDuration={3000}
        onClose={() => setIsToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setIsToastOpen(false)}
          severity={!isToastSuccess ? "error" : "success"}
          variant="filled"
          sx={{
            width: '100%', zIndex: '10000', fontSize: '14px',
            backgroundColor: !isToastSuccess ? '#ff7f7f' : 'rgb(50 182 144)',
          }}
        >
          {toastText}
        </Alert>
      </Snackbar>
  
    </Box>
  );
};

export default TableComponent;
